/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Template, TemplateType } from '@marageti/z4-sdk/lib/templates/Template';
import {
  EditorStateHandle,
  getSubmitButtonProps,
  useApiClient,
  useFetch,
} from '@marageti/z4-lib';
import { TravelerProfile } from '@marageti/z4-sdk/lib/people';
import useZicassoChat from '../../hooks/use-zicasso-chat';

const schema = z.object({
  message: z.string().min(1, { message: 'Message is required' }),
});

const simplifyString = (string: string) => string.toLowerCase().replace(/[^a-z0-9]/g, '');

const SMS_CHAR_LIMIT = 640;

const countSMSCharacters = (text: string): {
  count: number;
  maxLimit: number;
} => ({
  count: text.length,
  maxLimit: SMS_CHAR_LIMIT,
});

type SendSMSFormData = z.infer<typeof schema>;

const SendSMSModal = ({
  open,
  onClose,
  conversationId,
  travelerId,
  subscribeToRealtime,
}: {
  open: boolean;
  onClose: () => void;
  conversationId: string;
  travelerId?: string;
  subscribeToRealtime?: boolean;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState<Error | null>(null);

  const {
    sendSMS,
    updateMessages,
  } = useZicassoChat({
    conversationId,
    subscribeToRealtime: subscribeToRealtime !== false,
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    control,
    reset: resetForm,
    watch,
    setValue,
  } = useForm<SendSMSFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      message: '',
    },
  });

  const apiClient = useApiClient();
  const { data: templates } = useFetch<Template[]>(apiClient.templatesClient.getTemplates, [], true);
  const { data: travelerProfile } = useFetch<TravelerProfile>(
    apiClient.peopleClient.getTravelerProfile,
    [travelerId],
    open && !!travelerId,
  );
  const [templateValue, setTemplateValue] = useState<(Template & { group: string }) | null>(null);
  const editorRef = useRef<EditorStateHandle>(null);
  const isSmsOptedOut = travelerProfile?.preferences?.smsOptOut === true;

  const onChangeTemplate = (_e: React.SyntheticEvent, selectedTemplate: (Template & { group: string }) | null) => {
    setTemplateValue(selectedTemplate);
    const templateBody = selectedTemplate?.body || '';
    const plainText = templateBody
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<\/div>/gi, '\n')
      .replace(/<[^>]*>/g, '');

    const truncatedText = plainText.slice(0, SMS_CHAR_LIMIT);

    setValue('message', truncatedText, { shouldDirty: true, shouldValidate: true });
    editorRef.current?.setEditorState(truncatedText);
  };

  const sortedGroupedTemplates = useMemo(() => {
    if (!templates) return [];

    const templateGroups = [
      { type: TemplateType.PersonalTemplate, label: '--- Personal ---' },
      { type: TemplateType.PartnershipsTemplate, label: '--- Partnerships ---' },
      { type: TemplateType.CareTemplate, label: '--- Care ---' },
    ];

    return templateGroups.flatMap(({ type, label }) => templates
      .filter((template) => template.templateType === type)
      .sort((a, b) => a.title.localeCompare(b.title))
      .map((template) => ({
        ...template,
        group: label,
      })));
  }, [templates]);

  const reset = () => {
    setError(null);
    resetForm();
    setTemplateValue(null);
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  const onSubmit: SubmitHandler<SendSMSFormData> = async (data) => {
    const { message } = data;

    try {
      await sendSMS(message);
      await updateMessages();
      enqueueSnackbar('SMS sent successfully.', {
        variant: 'success',
      });
      handleClose();
    } catch (e) {
      setError(e as Error);
    }
  };

  const message = watch('message');

  const characterInfo = useMemo(() => {
    const plainText = message.replace(/<[^>]*>/g, '');
    const { count, maxLimit } = countSMSCharacters(plainText);
    return {
      count,
      maxLimit,
      remaining: maxLimit - count,
    };
  }, [message]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ px: 3 }}>Send SMS</DialogTitle>
      <DialogContent sx={{
        p: 0,
        m: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      >
        <Stack
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'hidden',
          }}
        >
          <Stack
            spacing={1.5}
            sx={{
              px: 3,
              pt: 1,
              pb: 2.5,
              flex: 1,
              overflowY: 'auto',
            }}
          >
            <Autocomplete
              options={sortedGroupedTemplates}
              groupBy={(option) => option.group}
              getOptionLabel={(option) => option.title || ''}
              value={templateValue}
              onChange={onChangeTemplate}
              disabled={isSmsOptedOut}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Template"
                  fullWidth
                />
              )}
              renderOption={({ key, ...props }, option, _, ownerState) => (
                <MenuItem key={key} {...props}>
                  <Typography variant="body1">{ownerState.getOptionLabel(option)}</Typography>
                </MenuItem>
              )}
              filterOptions={(options, params) => {
                const searchText = simplifyString(params.inputValue);
                return options.filter((option) => simplifyString(option.title).includes(searchText));
              }}
            />
            <Controller
              control={control}
              name="message"
              render={({ field }) => {
                const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  const newValue = e.target.value;
                  // Remove HTML tags to check plain text length
                  const plainText = newValue.replace(/<[^>]*>/g, '');

                  // Only allow the change if it doesn't exceed the SMS_CHAR_LIMIT
                  if (plainText.length <= SMS_CHAR_LIMIT) {
                    field.onChange(e);
                  }
                };

                return (
                  <TextField
                    {...field}
                    onChange={handleChange}
                    multiline
                    rows={10}
                    error={!!errors.message}
                    helperText={errors.message?.message}
                    label="Message"
                    disabled={isSmsOptedOut}
                    sx={{
                      height: '400px',
                      minHeight: '200px',
                      flex: 1,
                    }}
                  />
                );
              }}
            />
            <Typography variant="caption" color="text.secondary">
              This message will be sent to the primary traveler phone number.
              <br />
              {characterInfo.count.toLocaleString()}
              {' / '}
              {characterInfo.maxLimit.toLocaleString()}
              {' '}
              characters. Rich text not supported.
            </Typography>
            {isSmsOptedOut && (
              <Alert severity="error">
                <AlertTitle>Traveler has opted out of SMS</AlertTitle>
                We cannot send them a text.
              </Alert>
            )}
            {error && (
              <Alert severity="error">
                <AlertTitle>Failed to send SMS!</AlertTitle>
                {error.message}
              </Alert>
            )}
          </Stack>
          <Stack
            direction="row"
            spacing={2}
            sx={(theme) => ({
              borderTop: `1px solid ${theme.palette.divider}`,
              py: 2.5,
              px: 3,
              flexShrink: 0,
            })}
          >
            <LoadingButton
              type="submit"
              variant="contained"
              {...getSubmitButtonProps(isSubmitting || !open, isValid && !isSmsOptedOut)}
            >
              Submit
            </LoadingButton>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default SendSMSModal;
